<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <div class="space-y-4">
      <Input
        v-model="formData.full_name"
        type="text"
        label="Full name"
        placeholder="Enter your full name"
        required
        :error="errors.full_name"
        :disabled="isLoading"
        @blur="handleFullNameBlur"
      />

      <Input
        v-model="formData.email"
        type="email"
        label="Email address"
        placeholder="Enter your email"
        required
        :error="errors.email"
        :disabled="isLoading"
        @blur="handleEmailBlur"
      />

      <Input
        v-model="formData.password"
        type="password"
        label="Password"
        placeholder="Create a password"
        required
        :error="errors.password"
        :disabled="isLoading"
        @blur="handlePasswordBlur"
      />

      <PasswordStrengthIndicator :password="formData.password" :show-requirements="true" />

      <Input
        v-model="formData.confirmPassword"
        type="password"
        label="Confirm Password"
        placeholder="Confirm your password"
        required
        :error="errors.confirmPassword"
        :disabled="isLoading"
        @blur="handleConfirmPasswordBlur"
      />
    </div>

    <div class="flex items-start">
      <div class="flex items-center h-5">
        <input
          id="agree-terms"
          v-model="formData.agreeToTerms"
          name="agree-terms"
          type="checkbox"
          required
          class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
        />
      </div>
      <div class="ml-3 text-sm">
        <label for="agree-terms" class="text-gray-700">
          I agree to the
          <button
            type="button"
            @click="$emit('terms')"
            class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
          >
            Terms of Service
          </button>
          and
          <button
            type="button"
            @click="$emit('privacy')"
            class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
          >
            Privacy Policy
          </button>
        </label>
      </div>
    </div>

    <div>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        :loading="isLoading"
        :disabled="!isFormValid"
        full-width
      >
        Create account
      </Button>
    </div>

    <div class="text-center">
      <p class="text-sm text-gray-600">
        Already have an account?
        <button
          type="button"
          @click="$emit('login')"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Sign in
        </button>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { computed, reactive } from 'vue';
  import { Button, Input } from '@asabdev/asab-ui';
  import { useAuth } from '../composables/useAuth';
  import { AUTH_ERROR_CODES, getErrorMessage, isAuthError } from '@/lib/api/errors';
  import PasswordStrengthIndicator from './PasswordStrengthIndicator.vue';
  import type { RegisterFormData } from '@/types/auth';
  import type { AxiosError } from 'axios';

  interface Props {
    redirectTo?: string;
  }

  // Props are defined but not used in this component
  defineProps<Props>();

  const emit = defineEmits<{
    success: [user: unknown];
    error: [error: string];
    terms: [];
    privacy: [];
    login: [];
    'verification-needed': [email: string];
  }>();
  const { register, isLoading, error: authError, clearError } = useAuth();

  // Form data
  const formData = reactive<RegisterFormData & { agreeToTerms: boolean }>({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    agreeToTerms: false,
  });

  // Form validation errors
  const errors = reactive({
    full_name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  // Validation rules
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const nameRegex = /^[a-zA-Z\s'-]+$/;

  const validateFullName = (name: string): string => {
    if (!name) return 'Full name is required';
    if (name.length < 2) return 'Name must be at least 2 characters';
    if (name.length > 100) return 'Name must be less than 100 characters';
    if (!nameRegex.test(name))
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Password is required';
    if (password.length < 8) return 'Password must be at least 8 characters long';
    if (!/(?=.*[a-z])/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/(?=.*[A-Z])/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/(?=.*\d)/.test(password)) return 'Password must contain at least one number';
    return '';
  };

  const validateConfirmPassword = (password: string, confirmPassword: string): string => {
    if (!confirmPassword) return 'Please confirm your password';
    if (password !== confirmPassword) return 'Passwords do not match';
    return '';
  };

  // Computed properties
  const isFormValid = computed(() => {
    return (
      formData.full_name &&
      formData.email &&
      formData.password &&
      formData.confirmPassword &&
      formData.agreeToTerms &&
      !errors.full_name &&
      !errors.email &&
      !errors.password &&
      !errors.confirmPassword
    );
  });

  // Methods
  const validateForm = (): boolean => {
    errors.full_name = validateFullName(formData.full_name);
    errors.email = validateEmail(formData.email);
    errors.password = validatePassword(formData.password);
    errors.confirmPassword = validateConfirmPassword(formData.password, formData.confirmPassword);

    return !errors.full_name && !errors.email && !errors.password && !errors.confirmPassword;
  };

  const handleSubmit = async () => {
    // Clear previous auth errors
    clearError();

    // Validate form
    if (!validateForm()) {
      return;
    }

    if (!formData.agreeToTerms) {
      emit('error', 'Please agree to the Terms of Service and Privacy Policy');
      return;
    }

    try {
      const registrationData: RegisterFormData = {
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      };

      const response = await register(registrationData);

      // Ensure we pass the email back so it can be used for verification redirect
      const userWithEmail = {
        ...(typeof response === 'object' ? response : {}),
        email: response.user?.email || formData.email,
      };

      emit('success', userWithEmail);
    } catch (error) {
      const axErr = error as AxiosError<{
        error?: string;
        pending_approval?: boolean;
        waitlist_url?: string;
        support_email?: string;
      }>;
      // Re-register on pending account: surface the same waitlist banner
      // as a fresh signup. Reuses RegisterPage's handleRegisterSuccess
      // branch by emitting 'success' with pending_approval=true.
      if (isAuthError(axErr, AUTH_ERROR_CODES.REGISTER_PENDING_APPROVAL)) {
        const data = axErr.response?.data ?? {};
        emit('success', {
          email: formData.email,
          pending_approval: true,
          waitlist_url: data.waitlist_url ?? '',
          support_email: data.support_email ?? '',
        });
        return;
      }
      // Re-register on banned/suspended/deactivated → emit error so the
      // page can render a generic "this email is unavailable" message via
      // the canned ERROR_MESSAGES (no PII leak).
      if (
        isAuthError(axErr, AUTH_ERROR_CODES.REGISTER_ACCOUNT_BANNED) ||
        isAuthError(axErr, AUTH_ERROR_CODES.REGISTER_ACCOUNT_SUSPENDED) ||
        isAuthError(axErr, AUTH_ERROR_CODES.REGISTER_ACCOUNT_DEACTIVATED)
      ) {
        emit('error', getErrorMessage(axErr));
        return;
      }
      // Check if the error is due to user already existing
      if (isAuthError(error as AxiosError, AUTH_ERROR_CODES.REGISTER_USER_ALREADY_EXISTS)) {
        // User already exists, redirect to verification page
        emit('verification-needed', formData.email);
        return;
      }

      const errorMessage = authError.value || 'Registration failed. Please try again.';
      emit('error', errorMessage);
    }
  };

  // Real-time validation handlers
  const handleFullNameBlur = () => {
    if (formData.full_name) {
      errors.full_name = validateFullName(formData.full_name);
    }
  };

  const handleEmailBlur = () => {
    if (formData.email) {
      errors.email = validateEmail(formData.email);
    }
  };

  const handlePasswordBlur = () => {
    if (formData.password) {
      errors.password = validatePassword(formData.password);
    }
  };

  const handleConfirmPasswordBlur = () => {
    if (formData.confirmPassword) {
      errors.confirmPassword = validateConfirmPassword(formData.password, formData.confirmPassword);
    }
  };
</script>
